import { Alert, Box, Button, CircularProgress, LinearProgress, Typography } from '@mui/material';

import type { EligibleProvider } from './IntakeConfigPage';
import { IntakePageFooter } from './IntakePageFooter';
import { IntakePageHeader } from './IntakePageHeader';
import { cardSx, groupTitleSx, wf } from './tokens';

interface IntakeProviderRosterPageProps {
  providers: EligibleProvider[];
  isLoading?: boolean;
  isError?: boolean;
  onRetry?: () => void;
  onContinue: () => void;
  onBack: () => void;
  nextLabel: string;
  errorMessage?: string;
  embedded?: boolean;
}

export function IntakeProviderRosterPage({
  providers,
  isLoading = false,
  isError = false,
  onRetry,
  onContinue,
  onBack,
  nextLabel,
  errorMessage,
  embedded = false,
}: IntakeProviderRosterPageProps) {
  const hasProviders = providers.length > 0;
  const statusLabel = (status: EligibleProvider['status']): string => {
    if (status === 'complete') return 'Complete';
    if (status === 'in_progress') return 'In progress';
    if (status === 'invited') return 'Invited';
    return 'Unavailable';
  };

  return (
    <>
      {!embedded && (
        <IntakePageHeader
          title="Provider roster"
          sub="Confirm the providers who will complete their own intake."
        />
      )}

      <Box sx={cardSx}>
        <Box sx={{ ...groupTitleSx, marginBottom: '10px' }}>
          Providers eligible to complete intake
        </Box>
        <Typography sx={{ color: wf.muted, fontSize: '12.5px', marginBottom: '12px' }}>
          These providers were selected on Key Questions and will each complete their own intake.
        </Typography>

        {isLoading ? (
          <Box
            role="status"
            sx={{ display: 'flex', alignItems: 'center', gap: 1, color: wf.muted, py: 1 }}
          >
            <CircularProgress size={16} />
            Loading provider roster…
          </Box>
        ) : isError ? (
          <Alert
            severity="error"
            action={
              onRetry ? (
                <Button color="inherit" size="small" onClick={onRetry}>
                  Retry
                </Button>
              ) : undefined
            }
          >
            We couldn&apos;t load your provider roster. Please try again.
          </Alert>
        ) : hasProviders ? (
          <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
            {providers.map(provider => {
              const hasProgress =
                provider.percentComplete !== undefined && provider.status !== undefined;

              return (
                <Box
                  component="li"
                  key={provider.userId}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',
                      sm: 'minmax(180px, 1fr) minmax(180px, 1fr) auto',
                    },
                    alignItems: 'center',
                    gap: { xs: '6px', sm: '16px' },
                    py: '10px',
                    borderTop: `1px solid ${wf.lineSoft}`,
                  }}
                >
                  <Typography sx={{ color: wf.ink, fontSize: '13px', fontWeight: 650 }}>
                    {provider.name}
                  </Typography>
                  <Box>
                    {hasProgress ? (
                      <>
                        <LinearProgress
                          aria-label={`${provider.name} intake progress`}
                          variant="determinate"
                          value={provider.percentComplete}
                          sx={{
                            height: 6,
                            borderRadius: 4,
                            backgroundColor: wf.track,
                            '& .MuiLinearProgress-bar': { backgroundColor: wf.blue },
                          }}
                        />
                        <Typography sx={{ color: wf.muted, fontSize: '12px', mt: '2px' }}>
                          {provider.percentComplete}%
                        </Typography>
                      </>
                    ) : (
                      <Typography sx={{ color: wf.muted, fontSize: '12px' }}>
                        Progress unavailable
                      </Typography>
                    )}
                  </Box>
                  <Box
                    component="span"
                    sx={{
                      justifySelf: { xs: 'start', sm: 'end' },
                      borderRadius: '12px',
                      border: `1px solid ${provider.status === 'complete' ? 'transparent' : wf.line}`,
                      backgroundColor:
                        provider.status === 'complete' ? wf.greenSoft : wf.surfaceAlt,
                      color: provider.status === 'complete' ? wf.green : wf.muted,
                      fontSize: '11px',
                      fontWeight: 650,
                      px: '10px',
                      py: '3px',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {statusLabel(provider.status)}
                  </Box>
                </Box>
              );
            })}
          </Box>
        ) : (
          <Alert severity="warning">
            No providers have been added. Go back to Key Questions and add at least one provider.
          </Alert>
        )}
      </Box>

      <Alert severity="info">
        <Box sx={{ fontWeight: 650 }}>Payer exceptions</Box>
        <Box>
          Providers use your clinic payer list by default. Contact your JotPsych specialist only
          when a provider needs a different payer setup.
        </Box>
      </Alert>

      {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

      {!embedded && (
        <IntakePageFooter
          primaryLabel={nextLabel}
          onPrimary={onContinue}
          onBack={onBack}
          disabled={isLoading || isError}
          disabledReason={
            isError ? 'Retry loading your provider roster before continuing.' : undefined
          }
        />
      )}
    </>
  );
}
